import React, { useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";

/**
 * Searchable single-select used for "Județ" in lead + checkout forms.
 *
 * Plain string in/out (no objects) to keep the surrounding form logic simple.
 * Built on shadcn Popover + Command so screen-readers and keyboard navigation
 * work out of the box (Arrow up/down, Enter, Escape).
 */
export default function CountyCombobox({
  options,
  value,
  onChange,
  placeholder = "Selectează județul",
  searchPlaceholder = "Caută județ...",
  emptyMessage = "Niciun rezultat.",
  required = false,
  testId = "county-select",
  invalid = false,
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-required={required}
          data-testid={testId}
          className={`w-full flex items-center justify-between gap-2 rounded-xl border bg-[#F5F6F6] px-4 py-3 text-left text-[#373A45] hover:bg-white transition-colors ${
            invalid ? "border-red-400" : "border-[#E5E7EB]"
          } ${value ? "" : "text-[#9CA3AF]"}`}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown size={16} className="text-[#6B7280] shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[320px]"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? "" : opt);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${opt}`}
                >
                  <Check
                    size={14}
                    className={`mr-2 ${value === opt ? "opacity-100" : "opacity-0"}`}
                  />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
